import { useMemo } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import type { TeamMember } from "../../Booking-Form/typings/team-member";
import type { Dashboard2WeekDay } from "../types/dashboard2-work-status.types";
import { getPublicHolidayNameForDay } from "../utils/dashboard2-public-holidays.utils";
import {
  deriveDayWorkStatusForUser,
  getDashboard2WeekRange,
  selectPrimaryBookingForUserOnDay
} from "../utils/dashboard2-schedule.utils";
import { resolveDashboard2BookingTypeLabel } from "../utils/dashboard2-booking-type-label.utils";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";

type UseDashboard2WeekPlanDaysOptions = {
  weekAnchor: DateTime;
  schedules: BookingScheduleInterface[];
  currentUserId: string;
  storedColleagues: TeamMember[];
};

export function useDashboard2WeekPlanDays({
  weekAnchor,
  schedules,
  currentUserId,
  storedColleagues
}: UseDashboard2WeekPlanDaysOptions): Dashboard2WeekDay[] {
  const { t, i18n } = useTranslation();
  const { timeFormat } = useTimeFormat();
  const checkinSettings = useSelector(
    (state: RootState) => state.login.userInformation.company.meta.checkinSettings
  );
  const bookingsOnSaturdaysAllowed = useSelector(
    (state: RootState) => state.login.userInformation.company.meta.bookingsOnSaturdaysAllowed
  );
  const bookingsOnSundaysAllowed = useSelector(
    (state: RootState) => state.login.userInformation.company.meta.bookingsOnSundaysAllowed
  );

  return useMemo(() => {
    const { workDays } = getDashboard2WeekRange(weekAnchor, {
      bookingsOnSaturdaysAllowed,
      bookingsOnSundaysAllowed
    });
    const todayKey = DateTime.now().toFormat("yyyy-MM-dd");
    const deriveOptions = {
      mobileWorkingLabel: t("_dashboard2_statusMobileWorking"),
      notSetLabel: t("_dashboard2_statusNotSet"),
      checkinSettings,
      timeFormat,
      resolveBookingTypeLabel: (bookingType: string) =>
        resolveDashboard2BookingTypeLabel(bookingType, t),
      resolveExternalLabel: () => t("_dashboard2_bookingParticipantTagExternal")
    };

    return workDays.map(day => {
      const dayKey = day.toFormat("yyyy-MM-dd");
      const holidayName = getPublicHolidayNameForDay(day, i18n.language);
      const userStatus = deriveDayWorkStatusForUser(schedules, currentUserId, day, {
        ...deriveOptions,
        holidayName
      });
      const primarySchedule =
        userStatus.kind === "office" || userStatus.kind === "mobile_working"
          ? selectPrimaryBookingForUserOnDay(schedules, currentUserId, day)
          : undefined;

      const colleagues = storedColleagues.map(colleague => {
        const status = deriveDayWorkStatusForUser(schedules, colleague.userId, day, {
          ...deriveOptions,
          holidayName
        });

        return {
          colleagueId: colleague.userId,
          firstName: colleague.firstName ?? "",
          surname: colleague.surname ?? "",
          photoUrl: colleague.photoUrl ?? undefined,
          status
        };
      });

      return {
        dayKey,
        label: day.toFormat("ccc d LLL"),
        isToday: dayKey === todayKey,
        userStatus,
        primarySchedule,
        colleagues
      };
    });
  }, [
    bookingsOnSaturdaysAllowed,
    bookingsOnSundaysAllowed,
    checkinSettings,
    currentUserId,
    i18n.language,
    schedules,
    storedColleagues,
    t,
    timeFormat,
    weekAnchor
  ]);
}
